import { useEffect, useRef, useState } from "react";

function useCountUp(target, duration = 1600, run = true) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run || !target) return;
    let raf;
    const start = performance.now();
    const animate = (t) => {
      const elapsed = t - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, run]);
  return value;
}

function StatBlock({ label, value, suffix = "", testid }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setInView(true),
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const display = useCountUp(value, 1400, inView);
  return (
    <div ref={ref} data-testid={testid} className="flex flex-col gap-3">
      <p className="font-serif text-5xl md:text-6xl lg:text-7xl text-ink leading-none">
        {display.toLocaleString()}
        <span className="text-terracotta">{suffix}</span>
      </p>
      <p className="overline">{label}</p>
    </div>
  );
}

export default function Stats({ stats }) {
  const s = stats || {
    lives_touched: 0,
    communities_served: 0,
    active_projects: 0,
    volunteers: 0,
  };
  return (
    <section
      data-testid="stats-section"
      className="relative py-20 md:py-28 bg-sand"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-5">
            <p className="overline mb-5">By the numbers</p>
            <h2 className="text-4xl md:text-5xl text-ink leading-tight">
              Eighteen years of <em className="italic text-forest">showing up</em>, one street at a time.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 text-graphite md:text-lg leading-relaxed">
            Numbers are never the point — but they keep us honest. Here's what
            the work has looked like across 62 neighborhoods.
          </div>
        </div>

        <div className="divider-rule mb-12" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 md:gap-6">
          <StatBlock
            label="Lives touched"
            value={s.lives_touched}
            testid="stat-lives"
          />
          <StatBlock
            label="Communities served"
            value={s.communities_served}
            testid="stat-communities"
          />
          <StatBlock
            label="Active projects"
            value={s.active_projects}
            testid="stat-projects"
          />
          <StatBlock
            label="Active volunteers"
            value={s.volunteers}
            suffix="+"
            testid="stat-volunteers"
          />
        </div>
      </div>
    </section>
  );
}
